import type { SupplierCapitalEquipment } from '@tandemone/types'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import {
  createSupplierEquipment,
  deleteSupplierEquipment,
  getSupplierEquipment,
  updateSupplierEquipment
} from './api'
import { type FieldDef, ContactRowList } from './contact-row-list'
import { FormSkeleton } from './form-skeleton'
import { invalidateOnboarding } from './invalidate-onboarding'
import { PrimaryButton } from './primary-button'
import { StepFooter } from './step-footer'

const FIELDS: ReadonlyArray<FieldDef<SupplierCapitalEquipment>> = [
  {
    key: 'equipmentType',
    label: 'Equipment',
    required: true,
    placeholder: 'e.g. 5-axis CNC mill'
  },
  {
    key: 'quantity',
    label: 'Quantity',
    type: 'number',
    required: true,
    placeholder: '1'
  }
]

const EMPTY_DRAFT: Omit<SupplierCapitalEquipment, 'id'> = {
  equipmentType: '',
  quantity: 1
}

export function SupplierEquipmentForm({
  onSuccess,
  submitLabel = 'Save and continue'
}: {
  onSuccess: () => void
  submitLabel?: string
}) {
  const queryClient = useQueryClient()
  const invalidate = () =>
    invalidateOnboarding(queryClient, 'supplier-equipment')

  const { data, isLoading } = useQuery({
    queryKey: ['supplier-equipment'],
    queryFn: ({ signal }) => getSupplierEquipment(signal)
  })

  const addMutation = useMutation({
    mutationFn: createSupplierEquipment,
    onSuccess: invalidate
  })
  const updateMutation = useMutation({
    mutationFn: ({
      id,
      patch
    }: {
      id: string
      patch: Partial<Omit<SupplierCapitalEquipment, 'id'>>
    }) => updateSupplierEquipment(id, patch),
    onSuccess: invalidate
  })
  const removeMutation = useMutation({
    mutationFn: deleteSupplierEquipment,
    onSuccess: invalidate
  })

  if (isLoading) return <FormSkeleton />
  const rows = data?.equipment ?? []
  const canContinue = rows.length >= 1

  return (
    <div className='flex w-full flex-col gap-4'>
      <section className='rounded-2xl border border-border bg-page p-6'>
        <ContactRowList<SupplierCapitalEquipment>
          rows={rows}
          fields={FIELDS}
          emptyDraft={EMPTY_DRAFT}
          addLabel='Add another machine'
          emptyState='Add at least one piece of capital equipment'
          onAdd={draft => addMutation.mutateAsync(draft)}
          onSave={async (id, patch) => {
            await updateMutation.mutateAsync({ id, patch })
          }}
          onRemove={async id => {
            await removeMutation.mutateAsync(id)
          }}
        />
      </section>

      <StepFooter
        hint={
          rows.length === 0
            ? 'Add at least one item'
            : `${rows.length} item${rows.length === 1 ? '' : 's'}`
        }
      >
        <PrimaryButton
          type='button'
          disabled={!canContinue}
          onClick={onSuccess}
        >
          {submitLabel}
        </PrimaryButton>
      </StepFooter>
    </div>
  )
}
